import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOpenAlarmCounts } from '../hooks/useOpenAlarmCounts';

const getNavLinks = (t) => [
  { to: '/dashboard', icon: 'dashboard', label: t('navigation.dashboard') },
  { to: '/equipment', icon: 'devices_other', label: t('navigation.equipment') },
  { to: '/assistant', icon: 'smart_toy', label: t('navigation.assistant') },
  { to: '/regions-stats', icon: 'location_city', label: t('navigation.regionsStats') },
  { to: '/speedtest-results', icon: 'speed', label: t('navigation.speedtest') },
  { to: '/alerts', icon: 'notifications_active', label: t('navigation.alerts') },
  { to: '/panic-incidents', icon: 'crisis_alert', label: t('navigation.panic'), badgeKey: 'panic' },
  { to: '/fire-incidents', icon: 'local_fire_department', label: t('navigation.fire'), badgeKey: 'fire' },
  { to: '/logs', icon: 'list_alt', label: t('navigation.logs'), roles: ['engineer', 'admin'] },
  { to: '/admin', icon: 'admin_panel_settings', label: t('navigation.admin'), roles: ['admin'] },
];

const Sidebar = ({ onLogout, userRole = 'viewer', isSidebarVisible, onClose }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const navLinks = getNavLinks(t);
  const { panicCount, fireCount } = useOpenAlarmCounts(true);

  const badgeFor = (key) => {
    if (key === 'panic') return panicCount;
    if (key === 'fire') return fireCount;
    return 0;
  };

  return (
    <>
      {isSidebarVisible && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden top-[calc(4rem+0.75rem+env(safe-area-inset-top,0px))]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed z-40 flex flex-col justify-between bg-surface-1 text-muted-100 transition-all duration-300 ease-brand max-lg:bottom-0 max-lg:left-0 max-lg:right-0 max-lg:top-[calc(4rem+0.75rem+env(safe-area-inset-top,0px))] max-lg:overflow-y-auto lg:sticky lg:top-[81px] lg:z-30 lg:h-[calc(100dvh-81px)] lg:max-h-[calc(100dvh-81px)] lg:shrink-0 lg:self-start lg:overflow-y-auto ${
          isSidebarVisible
            ? 'translate-x-0 opacity-100 lg:w-[280px] lg:min-w-[280px]'
            : '-translate-x-full opacity-0 lg:translate-x-0 lg:opacity-100 lg:w-0 lg:min-w-0 lg:overflow-hidden'
        }`}
      >
      <div className="px-3 py-4 sm:px-4 sm:py-6">
        <nav className="flex flex-col gap-1">
          {navLinks
            .filter((link) => {
              if (!link.roles && !link.role) return true;
              const normalizedUserRole = userRole ? String(userRole).trim().toLowerCase() : 'viewer';
              if (link.role) {
                return String(link.role).trim().toLowerCase() === normalizedUserRole;
              }
              if (link.roles && Array.isArray(link.roles)) {
                return link.roles.some((role) =>
                  String(role).trim().toLowerCase() === normalizedUserRole
                );
              }
              return true;
            })
            .map((link) => {
              const badge = badgeFor(link.badgeKey);
              const isFireAlert = link.badgeKey === 'fire' && badge > 0;
              const isPanicAlert = link.badgeKey === 'panic' && badge > 0;
              const isActive = pathname === link.to || pathname.startsWith(`${link.to}/`);
              return (
                <button
                  key={link.to}
                  type="button"
                  onClick={() => {
                    navigate(link.to);
                    if (typeof window !== 'undefined' && window.innerWidth < 1024 && onClose) {
                      onClose();
                    }
                  }}
                  className={`group flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                    isFireAlert
                      ? 'alarm-nav-pulse bg-critical/15 text-white focus-visible:ring-critical'
                      : isPanicAlert
                        ? 'bg-warning/10 text-white focus-visible:ring-warning'
                        : isActive
                          ? 'bg-white/[0.08] text-white'
                          : 'text-muted-300 hover:bg-white/[0.06] hover:text-white'
                  }`}
                >
                  <span
                    className={`material-icons text-[20px] transition ${
                      isFireAlert
                        ? 'text-critical'
                        : isPanicAlert
                          ? 'text-warning'
                          : isActive
                            ? 'text-white'
                            : 'text-muted-400 group-hover:text-white'
                    }`}
                  >
                    {link.icon}
                  </span>
                  <span className="truncate">{link.label}</span>
                  {badge > 0 && (
                    <span
                      className={`ml-auto inline-flex min-h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                        isFireAlert
                          ? 'bg-critical text-white'
                          : 'bg-warning text-surface-base'
                      }`}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </button>
              );
            })}
        </nav>
      </div>

      <div className="px-3 py-4 sm:px-4 sm:py-6">
        <div className="mb-3 rounded-2xl bg-white/[0.06] px-3 py-3 text-xs text-muted-300">
          <p className="font-medium text-white">{t('navigation.attention')}</p>
          <p className="mt-1 text-[11px] text-muted-300">{t('navigation.speedtestSchedule')}</p>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-300 transition hover:bg-critical/10 hover:text-critical focus:outline-none focus-visible:ring-2 focus-visible:ring-critical"
        >
          <span className="material-icons text-[20px]">logout</span>
          {t('common.logoutSystem')}
        </button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
